import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import './App.css';
import LandingPage from './LandingPage';
import { Box, Button, Link, Modal, Stack, TextField, Typography } from '@mui/material';
import AffDashboard from './AffDashboard';
import PayPalPopup from './components/PayPalPopup';
import ManageSub from './ManageSub';
import MobileAppPopup from './components/MobileAppPopup';
import Pioneers from './Pioneers';
import PioneersStart from './PioneersStart';
import AlertsPage from './AlertsPage';
import PioneersDashboard from './PioneersDashboard';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import BlogIndex from './pages/blogs';
import BlogWrapper from './pages/blogs/utils/BlogWrapper';
import CompareIndex from './pages/compare';
import CompareWrapper from './pages/compare/utils/CompareWrapper';
import FeatureIndex from './pages/features';
import FeatureWrapper from './pages/features/utils/FeatureWrapper';
import SolutionIndex from './pages/solutions';
import SolutionWrapper from './pages/solutions/utils/SolutionWrapper';
import ToolWrapper from './pages/tools/ToolWrapper';
import NotFound from './pages/blogs/utils/NotFound';
import PricingPage from './pages/PricingPage';
import CollectorEmbed from './components/CollectorEmbed';

// Privacy Policy component
const PrivacyPolicy = () => {
  return (
    <div className="privacy-policy" style={{ padding: '120px 20px 40px 20px', maxWidth: '800px', margin: '0 auto' }}>
      {/* noindex on purpose: a legal page has no search value, but Google had it
          ranking pos ~7 for "marter login" — the highest-converting query on the
          property — because every non-prerendered route used to serve homepage HTML.
          follow is kept so link equity still flows. */}
      <Helmet>
        <title>Privacy Terms | Marter</title>
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://www.getmarter.com/privacy" />
      </Helmet>
      <Typography variant="h4" component="h1" gutterBottom align="center">
        Privacy Terms
      </Typography>
      <Typography variant="subtitle1" gutterBottom align="center">
        Effective Date: October 26, 2023
      </Typography>
      
      <Typography variant="h6" gutterBottom style={{ marginTop: '20px' }}>
        Introduction
      </Typography>
      <Typography paragraph>
        These Privacy Terms ("Provisions") govern the acquisition, utilization, and dissemination of
        personal data when you interact with or utilize the services provided by Marter ("we," "us,"
        or "our") through our website, application, or affiliated offerings, collectively referred to as
        the "Services."
      </Typography>
      <Typography paragraph>
        By engaging with or utilizing our Services, you grant your consent to the procedures
        delineated in these Provisions and agree to the gathering, utilization, and disclosure of your
        personal data as expounded below. If you dissent with these Provisions, kindly abstain from
        using our Services.
      </Typography>
      
      <Typography variant="h6" gutterBottom style={{ marginTop: '20px' }}>
        Information We Gather
      </Typography>
      <Typography paragraph>
        We may amass the ensuing personal data from you, either voluntarily furnished or through
        your interaction with our Services:
      </Typography>
      <Typography paragraph>
        1. Email Address: The collection of your email address is intended for disseminating
        crucial updates, notifications, and promotional material germane to our Services.
      </Typography>
      <Typography paragraph>
        2. Credit Card Data: Your credit card data is collected and securely stored via a
        reputable third-party payment processor like Stripe to streamline your purchases
        and facilitate payment processing for the products you request.
      </Typography>
      <Typography paragraph fontWeight="bold">
        3. Google Sheets Access: To enable the export of data to Google Sheets, we require
        access to your Google Sheets. This allows us to either create new Google Sheets
        or append data to existing ones. We will retrieve all your existing Google Sheets
        to let you choose where to append data in this case.
      </Typography>
      
      <Typography variant="h6" gutterBottom style={{ marginTop: '20px' }}>
        Utilization of Personal Data
      </Typography>
      <Typography paragraph>
        We leverage the personal data amassed for the ensuing objectives:
      </Typography>
      <Typography paragraph>
        • Service Provision: Your hidden products, telephone number, email address, and
        product particulars are employed to grant you access to our Services and to bestow
        functionality, including account management, order processing, and client support.
      </Typography>
      <Typography paragraph>
        • Communication: Your telephone number and email address may be employed to
        initiate communication concerning your account and responses to your inquiries.
      </Typography>
      
      <Typography variant="h6" gutterBottom style={{ marginTop: '20px' }}>
        Data Security
      </Typography>
      <Typography paragraph>
        We execute judicious security measures to safeguard your personal data from unauthorized
        access, revelation, modification, or obliteration. Nevertheless, no mode of internet
        transmission or electronic storage can warrant unalloyed security, and we are unable to
        vouchsafe complete security.
      </Typography>
      
      <Typography variant="h6" gutterBottom style={{ marginTop: '20px' }}>
        Retention of Personal Data
      </Typography>
      <Typography paragraph>
        We maintain your personal data for the duration essential to fulfill the objectives delineated
        in these Provisions, except in instances where the law requires or permits an extended
        retention period.
      </Typography>
      
      <Typography variant="h6" gutterBottom style={{ marginTop: '20px' }}>
        Disclosure of Personal Data
      </Typography>
      <Typography paragraph>
        We may reveal your personal data to third parties under the ensuing situations:
      </Typography>
      <Typography paragraph>
        • Service Providers: We may enlist reputable third-party service providers to aid in
        the provision or enhancement of our Services, including payment processors. These
        providers will gain access to your personal data but are bound by a commitment to
        preserve its confidentiality and employ it solely for purposes defined by us.
      </Typography>
      <Typography paragraph>
        • Legal Compliance: We may divulge your personal data when compelled by law or in
        response to valid legal requests, such as court orders or subpoenas.
      </Typography>
      
      <Typography variant="h6" gutterBottom style={{ marginTop: '20px' }}>
        Your Options and Entitlements
      </Typography>
      <Typography paragraph>
        You possess the ensuing options and entitlements regarding your personal data:
      </Typography>
      <Typography paragraph>
        • Access and Revision: You may access and revise your personal data by contacting us
        via the details furnished below.
      </Typography>
      <Typography paragraph>
        • Erasure: You may request the erasure of your personal data by reaching out to us
        using the details provided below. Nevertheless, please be aware that specific data
        may need to be retained for legal or legitimate business motives.
      </Typography>
      
      <Typography variant="h6" gutterBottom style={{ marginTop: '20px' }}>
        Contacting Us
      </Typography>
      <Typography paragraph>
        If you have queries, reservations, or grievances regarding our privacy practices or wish to
        avail yourself of your entitlements, kindly reach out to us at: info@getmarter.com
      </Typography>
      
      <Typography variant="h6" gutterBottom style={{ marginTop: '20px' }}>
        Modifications to these Provisions
      </Typography>
      <Typography paragraph>
        We may periodically revise these Privacy Provisions. The most current iteration of the
        Privacy Provisions will be published on our website. We encourage you to review these
        Provisions at intervals.
      </Typography>
      <Typography paragraph>
        By continuing to utilize our Services after the effective date of any modifications, you
        embrace and concur with the revised Privacy Provisions.
      </Typography>
      <Typography paragraph>
        We appreciate your diligence in reading and comprehending our privacy provisions.
      </Typography>
      <Typography paragraph>
        Marter (getmarter.com).
      </Typography>
    </div>
  );
};

// Main App with router
function AppWithRouter() {
  const [email, setEmail] = useState("");
  const [emailModal, setEmailModal] = useState(false);
  const [resumeSub, setResumeSub] = useState(false);
  const [silentJim, setSilentJim] = useState(false);
  const [yearly, setYearly] = useState(0);
  const [payPalModal, setPayPalModal] = useState(false);
  const [mobileAppPopup, setMobileAppPopup] = useState(false);
  
  const location = useLocation();
  
  useEffect(() => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);

    // Jim Landing Page
    if (urlParams.has("a") && urlParams.get('a') === "silentjim") {
      setSilentJim(true);
    }

    // Mobile app
    if (urlParams.has("downloadMobileApp") && urlParams.get('downloadMobileApp') === "1") {
      setMobileAppPopup(true);
    }

    // Check if the 'affiliate' parameter exists in the query string
    if (localStorage.getItem('affiliate') === null) {
      if (urlParams.has("a")) {
        localStorage.setItem("affiliate", urlParams.get('a'))
      } else if (urlParams.has("fbclid")) {
        localStorage.setItem("affiliate", "meta")
      } else if (urlParams.has("gclid")) {
        localStorage.setItem("affiliate", "google")
      }
    }
    if (urlParams.has("canceledPromotion")) {
      alert("Use the coupon code '10OFF' to get 10% OFF!")
      window.location.href = "https://www.getmarter.com/"
    }
    if (urlParams.has("resumeSub")) {
      setResumeSub(true);
      setEmailModal(true);
    }
    if (window.location.pathname === "/webinar") {
      window.location.href = "https://us22.list-manage.com/survey?u=1674faa86d4f825975148f4ea&id=255b024084&attribution=false"
    }
  }, []);

  const signUp = async (e) => {
    if (email === "") {
      alert("Please enter your email address to the left of the 'Start Free Trial' button.")
    } else {
      setPayPalModal(true);
    }
  };

  // Handle special URLs before routing
  if (location.pathname.includes("affiliate-dashboard")) {
    return (
      <div className="App" style={{
        width: '100%',
        backgroundImage: 'linear-gradient(180deg, rgba(255,202,24,0.25), #FFF)',
        backgroundSize: '100% 20%',
        backgroundRepeat: 'no-repeat',
        height: "100vh",
        marginTop: "-5vh"
      }}>
        <AffDashboard />
      </div>
    );
  }

  if (location.pathname.includes("manage-subscription")) {
    return (
      <div className="App" style={{
        width: '100%',
        backgroundImage: 'linear-gradient(180deg, rgba(255,202,24,0.25), #FFF)',
        backgroundSize: '100% 20%',
        backgroundRepeat: 'no-repeat',
        height: "100vh",
        marginTop: "-5vh"
      }}>
        <ManageSub />
      </div>
    );
  }

  if (location.pathname.includes("alerts")) {
    return (
      <div className="App" style={{
        width: '100%',
        backgroundImage: 'linear-gradient(180deg, rgba(255,202,24,0.25), #FFF)',
        backgroundSize: '100% 20%',
        backgroundRepeat: 'no-repeat',
        height: "100vh",
        marginTop: "-5vh"
      }}>
        <AlertsPage />
      </div>
    );
  }

  if (location.pathname.includes("pioneers/start")) {
    return (
      <div className="App" style={{
        width: '100%',
        backgroundImage: 'linear-gradient(180deg, rgba(255,202,24,0.25), #FFF)',
        backgroundSize: '100% 20%',
        backgroundRepeat: 'no-repeat',
        height: "100vh",
        marginTop: "-5vh"
      }}>
        <PioneersStart />
      </div>
    );
  }

  if (location.pathname.includes("pioneers-dashboard")) {
    return (
      <div className="App" style={{
        width: '100%',
        backgroundImage: 'linear-gradient(180deg, rgba(255,202,24,0.25), #FFF)',
        backgroundSize: '100% 20%',
        backgroundRepeat: 'no-repeat',
        height: "100vh",
        marginTop: "-5vh"
      }}>
        <PioneersDashboard />
      </div>
    );
  }

  if (location.pathname.includes("pioneers")) {
    return (
      <div className="App" style={{
        width: '100%',
        backgroundImage: 'linear-gradient(180deg, rgba(255,202,24,0.25), #FFF)',
        backgroundSize: '100% 20%',
        backgroundRepeat: 'no-repeat',
        height: "100vh",
        marginTop: "-5vh"
      }}>
        <Pioneers />
      </div>
    );
  }

  // Normal routing for main content. The signup Modal / PayPal / mobile-app
  // popups render outside <Routes> so pages other than "/" (e.g. /pricing)
  // can open them too.
  return (
    <>
    <Routes>
      <Route path="/" element={
        <div className="App">
          <LandingPage setMobileAppPopup={setMobileAppPopup} signUp={signUp} setEmail={setEmail} setEmailModal={setEmailModal} setYearly={setYearly} silentJim={silentJim} />
        </div>
      } />
      <Route path="/pricing" element={
        <PricingPage signUp={signUp} setEmail={setEmail} setEmailModal={setEmailModal} setYearly={setYearly} silentJim={silentJim} />
      } />
      <Route path="/blogs" element={<BlogIndex />} />
      {/* Consolidated into how-to-rank-higher-on-walmart. vercel.json serves the real 301;
          this is the client-side fallback so the URL never lands on a 404. */}
      <Route
        path="/blogs/seo-tips-to-rank-higher-on-walmart"
        element={<Navigate to="/blogs/how-to-rank-higher-on-walmart" replace />}
      />
      <Route path="/blogs/:blogId" element={<BlogWrapper />} />
      <Route path="/compare" element={<CompareIndex />} />
      <Route path="/compare/:compareId" element={<CompareWrapper />} />
      <Route path="/features" element={<FeatureIndex />} />
      <Route path="/features/:featureId" element={<FeatureWrapper />} />
      <Route path="/solutions" element={<SolutionIndex />} />
      <Route path="/solutions/:solutionId" element={<SolutionWrapper />} />
      <Route path="/tools/:toolId" element={<ToolWrapper />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/404" element={<NotFound />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
    <Modal
            open={emailModal}
            onClose={() => setEmailModal(false)}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
          >
            <Box sx={{ width: { xs: '100%', sm: 'auto' } }} style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'white',
              border: '2px solid #ffca18',
              borderRadius: "15px",
              boxShadow: 24,
              p: 4,
              display: "grid",
              alighItems: "center",
              justifyContent: "center"
            }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignSelf="center"
                spacing={1}
                useFlexGap
                sx={{ pt: 2, width: { xs: '100%', sm: 'auto' }, p: "20px" }}
                style={{ margin: "auto" }}
              >
                <TextField
                  id="outlined-basic"
                  hiddenLabel
                  size="small"
                  variant="outlined"
                  aria-label="Enter your email address"
                  placeholder="Your email address"
                  name="email"
                  inputProps={{
                    autocomplete: 'off',
                    ariaLabel: 'Enter your email address',
                  }}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <Button variant="contained" style={{ backgroundColor: "rgb(230,175,0)" }} onClick={(e) => signUp(e)}>
                  {resumeSub ? "Resume Subscription" : "Start your Free Trial"}
                </Button>
              </Stack>
              <Typography variant="caption" textAlign="center" sx={{ opacity: 0.8 }}>
                By clicking &quot;Start&quot; you agree to our&nbsp;
                <Link href="/terms.pdf" color="primary">
                  Terms & Conditions
                </Link>
                .
              </Typography>
            </Box>
          </Modal>

    <PayPalPopup affiliate={localStorage.getItem('affiliate')} payPalModal={payPalModal} setPayPalModal={setPayPalModal} yearly={yearly} email={email} />
    <MobileAppPopup mobileAppPopup={mobileAppPopup} setMobileAppPopup={setMobileAppPopup} />
    </>
  );
}

// Wrapper component to provide router context
function App() {
  return (
    <BrowserRouter>
      <AppWithRouter />
      <CollectorEmbed />
    </BrowserRouter>
  );
}

export default App;